import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Mail, RefreshCw, CheckCircle, XCircle, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { getAuthHeaders } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL + "/api";

export default function EmailLogsPage() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const limit = 20;

  const fetchLogs = async (skip = 0) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/email-logs?skip=${skip}&limit=${limit}`, { headers: getAuthHeaders() });
      setLogs(res.data.logs);
      setTotal(res.data.total);
    } catch {
      toast.error("Failed to load email logs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs(page * limit);
  }, [page]);

  if (loading && logs.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <RefreshCw className="animate-spin text-blue-500" size={32} />
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="min-h-screen p-8">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <Mail className="text-blue-400" size={28} />
              Email Delivery Logs
            </h1>
            <p className="text-zinc-500 mt-1">Track email notifications sent from qPro</p>
          </div>
          <Button onClick={() => fetchLogs(page * limit)} variant="outline" className="border-zinc-700 text-zinc-300">
            <RefreshCw size={16} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        <div className="grid grid-cols-3 gap-4 mb-6">
          <Card className="bg-zinc-900/50 border-white/5">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <span className="text-zinc-500 text-sm">Total Emails</span>
                <Mail className="text-blue-400" size={18} />
              </div>
              <p className="text-2xl font-bold text-white mt-1">{total}</p>
            </CardContent>
          </Card>
          <Card className="bg-emerald-500/10 border-emerald-500/20">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <span className="text-zinc-400 text-sm">Sent</span>
                <CheckCircle className="text-emerald-400" size={18} />
              </div>
              <p className="text-2xl font-bold text-white mt-1">
                {logs.filter(l => l.status === "sent").length}
              </p>
            </CardContent>
          </Card>
          <Card className="bg-red-500/10 border-red-500/20">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <span className="text-zinc-400 text-sm">Failed</span>
                <XCircle className="text-red-400" size={18} />
              </div>
              <p className="text-2xl font-bold text-white mt-1">
                {logs.filter(l => l.status === "failed").length}
              </p>
            </CardContent>
          </Card>
        </div>

        <Card className="bg-zinc-900/50 border-white/5">
          <CardHeader>
            <CardTitle className="text-lg text-white">Recent Emails ({total})</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {logs.length > 0 ? (
              <div className="divide-y divide-white/5">
                {logs.map((log) => (
                  <div key={log.id} className="p-4 hover:bg-white/5">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                          log.status === "sent" ? "bg-emerald-500/20" : "bg-red-500/20"
                        }`}>
                          {log.status === "sent" ? (
                            <CheckCircle className="text-emerald-400" size={16} />
                          ) : (
                            <XCircle className="text-red-400" size={16} />
                          )}
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-white">{log.subject}</p>
                          <p className="text-xs text-zinc-500 mt-1">
                            To: {log.to_emails.join(", ")}
                          </p>
                          {log.error_message && (
                            <p className="text-xs text-red-400 mt-1">Error: {log.error_message}</p>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-zinc-500">
                          {new Date(log.sent_at).toLocaleString()}
                        </p>
                        {log.comparison_id && (
                          <a 
                            href={`/result/${log.comparison_id}`}
                            className="text-xs text-blue-400 hover:underline flex items-center justify-end gap-1 mt-1"
                          >
                            View Comparison <ExternalLink size={10} />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-12 text-center text-zinc-500">
                <Mail size={48} className="mx-auto opacity-50 mb-4" />
                <p>No email logs yet</p>
              </div>
            )}
          </CardContent>
        </Card>

        {total > limit && (
          <div className="flex items-center justify-center gap-4 mt-6">
            <Button 
              onClick={() => setPage(Math.max(0, page - 1))} 
              disabled={page === 0}
              variant="outline" 
              className="border-zinc-700 text-zinc-300"
            >
              Previous
            </Button>
            <span className="text-zinc-500 text-sm">
              Page {page + 1} of {Math.ceil(total / limit)}
            </span>
            <Button 
              onClick={() => setPage(page + 1)} 
              disabled={(page + 1) * limit >= total}
              variant="outline" 
              className="border-zinc-700 text-zinc-300"
            >
              Next
            </Button>
          </div>
        )}
      </div>
    </motion.div>
  );
}
